import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getAgreementByPublicId,
  updateAgreement,
  getAllContactPersons,
  addServiceToAgreement,
  removeServiceFromAgreement,
  replaceAgreementContacts,
} from "../../api/Agreement";
import { getUsersByAgreement, deleteUser } from "../../api/Users";
import { deleteTenant, getAllTenantsForAgreement } from "../../api/Tenants";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";
import { useSidebar } from "../../providers/SidebarContext";
import DetailsTab from "../../components/agreement/DetailsTab";
import ServicesTab from "../../components/agreement/ServicesTab";
import SettingsTab from "../../components/agreement/SettingsTab";
import TenantsTab from "../../components/agreement/TenantsTab";
import HardwareAssortmentTab from "../../components/agreement/HardwareAssortmentTab";
import MultipleSelectDropdown from "../../components/MultipleSelectDropdown";

const EditAgreementPage = () => {
  const { syncData } = useSidebar();
  const { getAccessToken, user } = useAuth();
  const { agreementPublicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [agreement, setAgreement] = useState(null);
  const [originalAgreement, setOriginalAgreement] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loadingAgreement, setLoadingAgreement] = useState(true);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingContactPerson, setLoadingContactPerson] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(true);

  const [filteredTenants, setFilteredTenants] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [diflexContacts, setDiflexContacts] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [services, setServices] = useState([]);

  const contactOptions = filteredContacts.map((c) => c.name);
  const userOptions = filteredUsers
    .map((u) => u.displayName || u.userPrincipalName || u.email || null)
    .filter(Boolean);

  const existingContacts = agreement?.agreementContacts || [];

  const query = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(
    query.get("tab") ? query.get("tab") : "details"
  );

  useEffect(() => {
    if (agreementPublicId) {
      handleGetAgreement();
      handleGetTenantsByAgreement();
      handleGetUsersByAgreement();
      handleGetAllContactPersons();
    }
  }, [agreementPublicId]);

  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (agreement && originalAgreement) {
      const isEqual =
        JSON.stringify(agreement) === JSON.stringify(originalAgreement);
      setHasChanges(!isEqual);
    }
  }, [agreement, originalAgreement]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  }, [activeTab, location.pathname, navigate]);

  const handleGetAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAgreement = await getAgreementByPublicId(
        agreementPublicId,
        accessToken
      );

      console.log("Fetched agreement:", newAgreement);

      setAgreement(newAgreement);
      setOriginalAgreement(newAgreement);
      setCustomerContacts(newAgreement.customerContacts);
      setDiflexContacts(newAgreement.diflexContacts);

      setLoadingAgreement(false);
    } catch (error) {
      console.error("Failed to fetch agreement:", error);
      setLoadingAgreement(false);
    }
  };

  const handleGetTenantsByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const tenants = await getAllTenantsForAgreement(
        agreementPublicId,
        accessToken
      );
      setFilteredTenants(tenants);
      setLoadingTenant(false);
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
      setLoadingTenant(false);
    }
  };

  const handleGetUsersByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const users = await getUsersByAgreement(agreementPublicId, accessToken);

      setFilteredUsers(users);
      setSelectedUsers([]);
      setLoadingUsers(false);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setLoadingUsers(false);
    }
  };

  const handleGetAllContactPersons = async () => {
    try {
      const accessToken = await getAccessToken();
      const contactPerson = await getAllContactPersons(accessToken);

      const mapped = contactPerson.map((person) => ({
        ...person,
        contactPersonPublicId: person.contactPersonPublicId,
        contactPersonName: person.name,
        contactPersonEmail: person.email,
        contactType: "",
      }));

      setFilteredContacts(mapped);
      setSelectedContacts([]);
      setLoadingContactPerson(false);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      setLoadingContacts(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleSaveAgreement = async () => {
  setHasChanges(false);
  try {
    const accessToken = await getAccessToken();

    const updatedAgreement = {
      ...agreement,
      updatedBy: user?.username || "System",
      updatedDateTimeUTC: new Date().toISOString(),
    };
    await updateAgreement(updatedAgreement, accessToken);

    const payload = {
      diflexContacts: (diflexContacts || []).map(c => ({
        contactPersonPublicId: c.contactPersonPublicId,
        isPrimary: !!c.isPrimary,
      })),
      userContacts: (customerContacts || []).map(c => ({
        contactPersonPublicId: c.contactPersonPublicId,
      })),
    };

    const result = await replaceAgreementContacts(
      agreementPublicId,
      payload,
      accessToken
    );

    setDiflexContacts(result.diflexContacts || []);
    setCustomerContacts(result.customerContacts || []);

    // 5) Hantera services (oförändrat)
    const agreementServicesToRemove = [];
    const agreementServicesToAdd = [];

    services.forEach((service) => {
      if (service.hasUpdatedIsActiveForAgreement) {
        if (!service.isActiveForAgreement) {
          agreementServicesToRemove.push(
            removeServiceFromAgreement(
              agreementPublicId,
              service.agreementServicePublicId,
              accessToken
            )
          );
        }
      } else if (service.hasUpdatedComment) {
        agreementServicesToRemove.push(
          removeServiceFromAgreement(
            agreementPublicId,
            service.agreementServicePublicId,
            accessToken
          )
        );
      }

      if (service.hasUpdatedIsActiveForAgreement) {
        if (service.isActiveForAgreement) {
          agreementServicesToAdd.push(
            addServiceToAgreement(
              {
                agreementPublicId,
                agreementServicePublicId: service.agreementServicePublicId,
                comment: service.comment ? service.comment : "",
              },
              accessToken
            )
          );
        }
      } else if (service.hasUpdatedComment) {
        agreementServicesToAdd.push(
          addServiceToAgreement(
            {
              agreementPublicId,
              agreementServicePublicId: service.agreementServicePublicId,
              comment: service.comment ? service.comment : "",
            },
            accessToken
          )
        );
      }
    });

    await Promise.all(agreementServicesToRemove);
    await Promise.all(agreementServicesToAdd);

    toast.success("Agreement updated successfully", {
      iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
    });

    window.dispatchEvent(new Event("agreementUpdated"));
    setHasChanges(false);
  } catch (error) {
    console.error("Update error:", error.response?.data || error.message);
    toast.error(
      `Error: ${error.response?.data?.message || "Failed to update agreement"}`
    );
    // Ladda om från servern för att visa korrekt state
    handleGetAgreement();
    setHasChanges(true);
  }
};

  const handleDeleteTenant = async (tenantPublicId) => {
    const toastId = toast.loading("Deleting tenant...");
    try {
      const accessToken = await getAccessToken();
      await deleteTenant(tenantPublicId, accessToken);
      handleGetTenantsByAgreement();
      syncData();

      toast.success("Tenant deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      toast.error("Failed to delete tenant", { id: toastId });
      console.error(error);
    }
  };

/*   const handleDeleteUser = async (userPublicId) => {
    const toastId = toast.loading("Deleting user...");
    try {
      const accessToken = await getAccessToken();
      await deleteUser(userPublicId, accessToken);
      handleGetUsersByAgreement();

      toast.success("User deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      toast.error("Failed to delete user", { id: toastId });
      console.error(error);
    }
  }; */

  const handleSelectContact = (selectedNames) => {
    const matched = filteredContacts.filter((c) =>
      selectedNames.includes(c.name)
    );

    const reformated = matched.map((c) => {
      const existingContact = diflexContacts.find(
        (dc) => dc.contactPersonPublicId === c.contactPersonPublicId
      );

      if (existingContact) {
        return {
          ...existingContact,
          isNew: false,
          isDeleted: false,
        };
      }
      return {
        contactPersonPublicId: c.contactPersonPublicId,
        contactPersonName: c.name,
        contactPersonEmail: c.email,
        contactType: "diflex",
        isNew: true,
        isDeleted: false,
      };
    });

    const existingContacts = diflexContacts.filter(
      (dc) =>
        !reformated
          .map((c) => c.contactPersonPublicId)
          .includes(dc.contactPersonPublicId)
    );

    if (reformated.length > 0) setHasChanges(true);
    setDiflexContacts([...existingContacts, ...reformated]);
  };

// Diflex
const handleDeleteContact = (contactPersonPublicId) => {
  setDiflexContacts(prev =>
    prev.filter(c => c.contactPersonPublicId !== contactPersonPublicId)
  );
  setHasChanges(true);
};

  const handleSelectCustomerContact = (selectedNames) => {
    const matched = filteredUsers.filter((u) =>
      selectedNames.includes(u.displayName || u.userPrincipalName || u.email)
    );

    const mapped = matched.map((user) => {
      const existingContact = customerContacts.find(
        (dc) => dc.contactPersonPublicId === user.userPublicId
      );
      if (existingContact) {
        return {
          ...existingContact,
          isNew: false,
          isDeleted: false,
        };
      }
      return {
        contactPersonPublicId: user.userPublicId,
        contactPersonName:
          user.displayName || user.userPrincipalName || user.email,
        contactPersonEmail: user.email || user.userPrincipalName,
        contactType: "customer",
        isNew: true,
        isDeleted: false,
      };
    });

    const existingContacts = customerContacts.filter(
      (dc) =>
        !mapped
          .map((c) => c.contactPersonPublicId)
          .includes(dc.contactPersonPublicId)
    );

    if (mapped.length > 0) setHasChanges(true);
    setCustomerContacts([...existingContacts, ...mapped]);
  };

  // Customer
const handleDeleteCustomerContact = (contactPersonPublicId) => {
  setCustomerContacts(prev =>
    prev.filter(c => c.contactPersonPublicId !== contactPersonPublicId)
  );
  setHasChanges(true);
};

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className={`${loadingAgreement ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit Agreement: {agreement?.name}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleSaveAgreement}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="border-b border-mute-200 mb-4 gap-4 flex">
              <button
                className={`px-4 pb-2 ${
                  activeTab === "details"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 pb-2 ${
                  activeTab === "tenants"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("tenants")}
              >
                Tenants
              </button>
              <button
                className={`px-4 pb-2 ${
                  activeTab === "services"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("services")}
              >
                Services
              </button>
              <button
                className={`px-4 pb-2 ${
                  activeTab === "team"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("team")}
              >
                Team
              </button>

              <button
                className={`px-4 pb-2 ${
                  activeTab === "settings"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("settings")}
              >
                Settings
              </button>

              <button
                className={`px-4 pb-2 ${
                  activeTab === "hardware"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                }`}
                onClick={() => setActiveTab("hardware")}
              >
                Hardware
              </button>
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              {activeTab === "details" && (
                <DetailsTab
                  agreement={agreement}
                  setAgreement={setAgreement}
                  setHasChanges={setHasChanges}
                />
              )}

              {agreementPublicId && activeTab === "hardware" && (
                <HardwareAssortmentTab agreementPublicId={agreementPublicId} />
              )}

              {agreementPublicId && activeTab === "tenants" && (
                <TenantsTab
                  agreementPublicId={agreementPublicId}
                  agreement={agreement}
                  filteredTenants={filteredTenants}
                  selectedTenants={selectedTenants}
                  setSelectedTenants={setSelectedTenants}
                  handleDeleteTenant={handleDeleteTenant}
                />
              )}

              {activeTab === "services" && (
                <ServicesTab
                  agreementPublicId={agreementPublicId}
                  services={services}
                  setServices={setServices}
                  setHasChanges={setHasChanges}
                />
              )}

              {agreementPublicId && activeTab === "team" && (
                <>
                  <div className="flex justify-between items-center">
                    <h3>Diflex contacts</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-6 w-full">
                    <MultipleSelectDropdown
                      label="Available Diflex contacts"
                      options={contactOptions}
                      multiSelect={true}
                      searchable={true}
                      withAddButton={true}
                      selectedOptions={selectedContacts}
                      setSelectedOptions={setSelectedContacts}
                      onSelect={handleSelectContact}
                    />
                    <TableView
                      data={diflexContacts.filter((c) => !c.isDeleted)}
                      setSelectedRows={() => {}}
                      noCheckbox={true}
                      noDelete={false}
                      noEdit={true}
                      handleDelete={(id) => handleDeleteContact(id)}
                      radioColumnKey="isPrimary"
                      radioGroupName="diflexContact"
                      onRadioSelect={(selectedId) => {
                        setDiflexContacts((prev) =>
                          prev.map((c) => ({
                            ...c,
                            isPrimary: c.contactPersonPublicId === selectedId,
                          }))
                        );
                        setHasChanges(true);
                      }}
                      columns={[
                        { key: "isPrimary", label: "Primary contact" },
                        { key: "contactPersonName", label: "Diflex contact" },
                        { key: "contactPersonEmail", label: "Email" },
                      ]}
                      primaryKey="contactPersonPublicId"
                      disableLink={true}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <h3>Customer contacts</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-6 w-full">
                    <MultipleSelectDropdown
                      label="Available User contacts"
                      options={userOptions}
                      multiSelect={true}
                      searchable={true}
                      withAddButton={true}
                      selectedOptions={selectedUsers}
                      setSelectedOptions={setSelectedUsers}
                      onSelect={handleSelectCustomerContact}
                    />

                    <TableView
                      data={customerContacts.filter((c) => !c.isDeleted)}
                      setSelectedRows={() => {}}
                      noCheckbox={true}
                      noDelete={false}
                      noEdit={true}
                      handleDelete={(id) => handleDeleteCustomerContact(id)}
                      columns={[
                        { key: "contactPersonName", label: "Customer contact" },
                        { key: "contactPersonEmail", label: "Email" },
                      ]}
                      primaryKey="contactPersonPublicId"
                      disableLink={true}
                      showActions={true}
                    />
                  </div>
                </>
              )}

              {agreementPublicId && activeTab === "settings" && (
                <SettingsTab
                  agreement={agreement}
                  setAgreement={setAgreement}
                  setHasChanges={setHasChanges}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default EditAgreementPage;
